/** Metres per degree of latitude, and of longitude once corrected by cos(lat). The one
 *  copy: the trail, the glide and the map's own placement maths all have to agree. */
export const METRES_PER_DEGREE = 111_320

export interface GeoPoint {
  lon: number
  lat: number
}

export interface TrainPose extends GeoPoint {
  bearing: number
}

export interface TrailPoint extends GeoPoint {
  /** Cumulative distance from the first retained point, in metres. */
  d: number
}

export interface CarPose extends TrainPose {
  index: number
}

/** Fast equirectangular distance; sufficiently accurate over a subway consist. */
export function metresBetween(a: GeoPoint, b: GeoPoint): number {
  const cosLat = Math.cos(((a.lat + b.lat) / 2) * Math.PI / 180)
  const dx = (b.lon - a.lon) * cosLat
  const dy = b.lat - a.lat
  return Math.hypot(dx, dy) * METRES_PER_DEGREE
}

export function bearingBetween(a: GeoPoint, b: GeoPoint): number {
  const dLon = (b.lon - a.lon) * Math.PI / 180
  const phi1 = a.lat * Math.PI / 180
  const phi2 = b.lat * Math.PI / 180
  const y = Math.sin(dLon) * Math.cos(phi2)
  const x = Math.cos(phi1) * Math.sin(phi2) - Math.sin(phi1) * Math.cos(phi2) * Math.cos(dLon)
  return ((Math.atan2(y, x) * 180) / Math.PI + 360) % 360
}

export function offsetPoint(point: GeoPoint, bearing: number, distanceM: number): GeoPoint {
  const radians = bearing * Math.PI / 180
  const d = distanceM / METRES_PER_DEGREE
  const cosLat = Math.cos(point.lat * Math.PI / 180) || 1
  return {
    lon: point.lon + d * Math.sin(radians) / cosLat,
    lat: point.lat + d * Math.cos(radians),
  }
}

/**
 * Creates oldest-to-newest distance history ending at `head`. If the known path
 * is shorter than a full consist, it is extended behind the oldest point. This
 * makes every car placeable immediately, including while the train is stopped.
 */
export function seedTrail(
  path: readonly GeoPoint[],
  head: GeoPoint,
  headBearing: number,
  requiredLengthM: number,
): TrailPoint[] {
  const points: GeoPoint[] = []
  for (const point of path) {
    const previous = points[points.length - 1]
    if (!previous || metresBetween(previous, point) > 0.01) points.push(point)
  }
  const last = points[points.length - 1]
  if (!last || metresBetween(last, head) > 0.01) points.push(head)
  if (points.length === 0) points.push(head)

  const trail: TrailPoint[] = []
  let distance = 0
  for (let i = 0; i < points.length; i++) {
    if (i > 0) distance += metresBetween(points[i - 1], points[i])
    trail.push({ ...points[i], d: distance })
  }

  const span = trail[trail.length - 1].d - trail[0].d
  if (span < requiredLengthM) {
    const oldest = trail[0]
    const next = trail[1]
    const forwardBearing = next ? bearingBetween(oldest, next) : headBearing
    const extension = offsetPoint(oldest, forwardBearing + 180, requiredLengthM - span)
    trail.unshift({ ...extension, d: oldest.d - (requiredLengthM - span) })
  }
  return trail
}

/** Records meaningful movement and bounds retained history by physical length. */
export function recordTrailPoint(
  trail: TrailPoint[],
  head: GeoPoint,
  minimumStepM: number,
  keepLengthM: number,
): boolean {
  const newest = trail[trail.length - 1]
  if (!newest) {
    trail.push({ ...head, d: 0 })
    return true
  }
  const step = metresBetween(newest, head)
  if (step < minimumStepM) return false
  trail.push({ ...head, d: newest.d + step })
  while (trail.length > 2 && trail[trail.length - 1].d - trail[1].d > keepLengthM) trail.shift()
  return true
}

/**
 * Samples a physical distance behind the live head. `head` is treated as a
 * virtual newest trail point, so followers move every frame instead of waiting
 * for the next persisted breadcrumb. If history is unexpectedly short, the path
 * is extrapolated behind its oldest tangent rather than clamping cars together.
 */
export function sampleBehind(
  trail: readonly TrailPoint[] | undefined,
  head: TrainPose,
  backM: number,
  cursor?: TrailCursor,
): TrainPose {
  if (!trail?.length || backM <= 0) {
    const point = backM > 0 ? offsetPoint(head, head.bearing + 180, backM) : head
    return { ...point, bearing: head.bearing }
  }

  const newest = trail[trail.length - 1]
  const headGap = metresBetween(newest, head)
  const headDistance = newest.d + headGap
  const target = headDistance - backM

  if (target >= newest.d && headGap > 0.001) {
    const fraction = (target - newest.d) / headGap
    return {
      lon: newest.lon + (head.lon - newest.lon) * fraction,
      lat: newest.lat + (head.lat - newest.lat) * fraction,
      bearing: bearingBetween(newest, head),
    }
  }

  for (
    let i = Math.min(cursor ? cursor.index : trail.length - 1, trail.length - 1);
    i > 0;
    i--
  ) {
    const a = trail[i - 1]
    const b = trail[i]
    if (target < a.d) continue
    if (cursor) cursor.index = i
    const span = b.d - a.d
    const fraction = span > 0 ? (target - a.d) / span : 0
    return {
      lon: a.lon + (b.lon - a.lon) * fraction,
      lat: a.lat + (b.lat - a.lat) * fraction,
      bearing: bearingBetween(a, b),
    }
  }

  if (cursor) cursor.index = 1
  const oldest = trail[0]
  const next = trail[1]
  const forwardBearing = next ? bearingBetween(oldest, next) : head.bearing
  const point = offsetPoint(oldest, forwardBearing + 180, oldest.d - target)
  return { ...point, bearing: forwardBearing }
}

/**
 * Scan position into a trail, so a run of INCREASING `backM` queries walks it once.
 * Start at the newest end; `sampleBehind` only ever moves it toward older points, which
 * is sound precisely because each successive car asks for a smaller target distance.
 */
export type TrailCursor = { index: number }

/**
 * The live `head` is car zero's nominal centreline anchor; every other nominal car
 * centre is a fixed arc distance behind it.
 * When `truckCentresM` is supplied, the body is constrained by two points on the trail:
 * one beneath each truck. Its origin is their midpoint and its bearing is the chord from
 * the rear truck to the front truck. This prevents a long rigid car from pivoting around
 * one centre tangent as it crosses a corner.
 *
 * `lateralOffsetM` is applied to each truck along that point's own normal before the body
 * pose is derived, so both trucks follow the offset route lane through a curve.
 * `headPathBearing` separates that normal from the visually eased model bearing: rotation
 * may lag through a turn, but position must remain on the line's instantaneous offset lane.
 *
 * The lateral shift belongs here rather than at the call sites: putting the drawn-route
 * lane offset on a consist is one operation, and doing it twice (the frame loop and the
 * filter-change rebuild) had already started to drift apart.
 *
 * One backward walk serves the whole consist — see TrailCursor. Per car it was O(trail),
 * which for the watched train's 0.12 m breadcrumbs meant seven scans of a ~1,750-point
 * history every frame, on the one train the camera is locked to.
 */
export function layoutTrailingConsist(
  head: TrainPose,
  trail: readonly TrailPoint[] | undefined,
  carCount: number,
  spacingM: number,
  truckCentresM = 0,
  lateralOffsetM = 0,
  headPathBearing = head.bearing,
): CarPose[] {
  const count = Math.max(1, Math.floor(carCount))
  const spacing = Math.max(0, spacingM)
  const truckHalfSpacing = Math.max(0, truckCentresM) / 2
  const centreCursor: TrailCursor = { index: trail ? trail.length - 1 : 0 }
  const frontTruckCursor: TrailCursor = { index: trail ? trail.length - 1 : 0 }
  const rearTruckCursor: TrailCursor = { index: trail ? trail.length - 1 : 0 }
  const cars: CarPose[] = []
  const pathHead: TrainPose = { ...head, bearing: headPathBearing }

  // The lead car's front truck is ahead of the newest breadcrumb. Continue along the
  // already-smoothed head bearing for that short distance; every other truck samples
  // recorded path history.
  const sampleAt = (backM: number, cursor: TrailCursor): TrainPose => {
    if (backM < 0) {
      const point = offsetPoint(pathHead, pathHead.bearing, -backM)
      return { ...point, bearing: pathHead.bearing }
    }
    return sampleBehind(trail, pathHead, backM, cursor)
  }

  const placeOnLane = (pose: TrainPose): TrainPose => {
    if (!lateralOffsetM) return pose
    const point = offsetPoint(pose, pose.bearing + 90, lateralOffsetM)
    return { ...point, bearing: pose.bearing }
  }

  for (let index = 0; index < count; index++) {
    const centreBackM = index * spacing
    const centre = index === 0
      ? head
      : sampleBehind(trail, pathHead, centreBackM, centreCursor)

    if (truckHalfSpacing <= 0) {
      const placed = placeOnLane(index === 0 ? { ...centre, bearing: headPathBearing } : centre)
      cars.push({ lon: placed.lon, lat: placed.lat, bearing: centre.bearing, index })
      continue
    }

    const frontTruck = placeOnLane(sampleAt(centreBackM - truckHalfSpacing, frontTruckCursor))
    const rearTruck = placeOnLane(sampleAt(centreBackM + truckHalfSpacing, rearTruckCursor))
    const truckChordM = metresBetween(rearTruck, frontTruck)
    const bearing = truckChordM > 0.001
      ? bearingBetween(rearTruck, frontTruck)
      : centre.bearing
    cars.push({
      lon: (frontTruck.lon + rearTruck.lon) / 2,
      lat: (frontTruck.lat + rearTruck.lat) / 2,
      bearing,
      index,
    })
  }
  return cars
}
